import React, { useState, useEffect } from 'react';
import { openExternal } from '../platform/NativeBridge';

// ── Keyframes ─────────────────────────────────────────────────────────────────
let _kfDone = false;
function injectKF() {
  if (_kfDone || document.getElementById('igc-kf')) return;
  _kfDone = true;
  const s = document.createElement('style');
  s.id = 'igc-kf';
  s.textContent = `
    @keyframes igcShimmer { 0%,100%{background-position:-200% 0} 50%{background-position:200% 0} }
    @keyframes igcFadeUp  { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:translateY(0)} }
    @keyframes igcDot     { 0%,80%,100%{transform:scale(0.5);opacity:0.3} 40%{transform:scale(1);opacity:1} }
    @keyframes igcPulse   { 0%,100%{opacity:0.4} 50%{opacity:1} }
  `;
  document.head.appendChild(s);
}

const STEPS = [
  'Processando prompt...',
  'Renderizando composição...',
  'Aplicando detalhes finais...',
  'Finalizando imagem...',
];

function providerLabel(provider) {
  if (provider === 'fal_flux_schnell') return 'FLUX.1 schnell';
  if (provider === 'athena_image_provider') return 'Provedor Athena';
  return provider || null;
}

// ── Loading view ──────────────────────────────────────────────────────────────
function LoadingView({ height }) {
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setStepIdx(i => (i + 1) % STEPS.length);
    }, 2800);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{
      height, borderRadius: 12, overflow: 'hidden',
      background: 'rgba(6,14,40,0.7)',
      border: '1px solid rgba(56,189,248,0.1)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 18,
      position: 'relative',
    }}>
      {/* Shimmer layer */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(90deg,rgba(56,189,248,0.03) 0%,rgba(56,189,248,0.08) 50%,rgba(56,189,248,0.03) 100%)',
        backgroundSize: '200% 100%',
        animation: 'igcShimmer 2.2s ease infinite',
      }} />

      {/* Icon */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
          style={{ opacity: 0.4, animation: 'igcPulse 2s ease infinite' }}>
          <rect x="3" y="3" width="18" height="18" rx="3" stroke="rgba(56,189,248,0.8)" strokeWidth="1.5"/>
          <circle cx="8.5" cy="8.5" r="1.5" fill="rgba(56,189,248,0.8)"/>
          <path d="M3 16.5L8 11.5L11 14.5L14.5 10L21 16.5" stroke="rgba(56,189,248,0.8)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* Step text */}
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <div style={{ fontSize: 12.5, color: 'rgba(172,196,236,0.7)', marginBottom: 10, animation: 'igcFadeUp .3s ease' }}
          key={stepIdx}>
          {STEPS[stepIdx]}
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
          {[0, 1, 2].map(d => (
            <div key={d} style={{
              width: 5, height: 5, borderRadius: '50%',
              background: '#38bdf8',
              animation: `igcDot 1.2s ease ${d * 0.2}s infinite`,
            }} />
          ))}
        </div>
      </div>

      {/* Step list */}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-start' }}>
        {STEPS.slice(0, -1).map((step, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{
              width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9,
              background: i < stepIdx ? 'rgba(16,185,129,0.25)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${i < stepIdx ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.1)'}`,
              color: i < stepIdx ? '#10b981' : 'rgba(255,255,255,0.2)',
              transition: 'all .3s ease',
            }}>
              {i < stepIdx ? '✓' : '·'}
            </span>
            <span style={{
              fontSize: 11, lineHeight: 1,
              color: i < stepIdx ? 'rgba(16,185,129,0.7)' : i === stepIdx ? 'rgba(172,196,236,0.55)' : 'rgba(100,116,139,0.4)',
              transition: 'color .3s ease',
            }}>
              {step.replace('...', '')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Error view ────────────────────────────────────────────────────────────────
function ErrorView({ prompt, height }) {
  function retry() {
    const p = prompt ? `Gere uma imagem: ${prompt.split(',')[0]?.trim() || prompt.slice(0, 80)}` : 'Gere uma imagem visual para este tema.';
    window.dispatchEvent(new CustomEvent('athena-quick-action', { detail: { prompt: p } }));
  }
  return (
    <div style={{
      height, borderRadius: 12,
      background: 'rgba(239,68,68,0.04)',
      border: '1px solid rgba(239,68,68,0.15)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 12,
    }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="rgba(239,68,68,0.5)" strokeWidth="1.5"/>
        <path d="M12 8v5" stroke="rgba(239,68,68,0.7)" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="12" cy="16" r="1" fill="rgba(239,68,68,0.7)"/>
      </svg>
      <span style={{ fontSize: 12.5, color: 'rgba(239,68,68,0.7)' }}>Falha ao gerar imagem</span>
      <button onClick={retry} style={{
        padding: '6px 16px', fontSize: 11.5,
        background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
        color: 'rgba(239,68,68,0.8)', borderRadius: 8,
        cursor: 'pointer', fontFamily: 'inherit',
      }}>
        Tentar novamente
      </button>
    </div>
  );
}

// ── Success view ──────────────────────────────────────────────────────────────
function SuccessView({ imageUrl, prompt, provider, isMobile }) {
  const [loaded, setLoaded] = useState(false);
  const [error,  setError]  = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { setLoaded(false); setError(false); }, [imageUrl]);

  const maxH = isMobile ? 240 : 340;

  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(56,189,248,0.12)', background: 'rgba(6,14,40,0.45)', animation: 'igcFadeUp .4s ease' }}>
      {/* Skeleton while loading */}
      {!loaded && !error && (
        <div style={{
          height: maxH,
          background: 'linear-gradient(90deg,rgba(56,189,248,0.04) 0%,rgba(56,189,248,0.09) 50%,rgba(56,189,248,0.04) 100%)',
          backgroundSize: '200% 100%',
          animation: 'igcShimmer 1.8s ease infinite',
        }} />
      )}

      <img
        src={imageUrl}
        alt={prompt?.split(',')[0] || 'Imagem gerada'}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        style={{ width: '100%', display: loaded ? 'block' : 'none', maxHeight: maxH, objectFit: 'cover' }}
      />

      {error && (
        <div style={{ padding: '14px', textAlign: 'center', color: 'rgba(239,68,68,0.6)', fontSize: 12 }}>
          Não foi possível carregar a imagem
        </div>
      )}

      {/* Footer */}
      {loaded && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 10px', borderTop: '1px solid rgba(56,189,248,0.07)' }}>
          {provider && (
            <span style={{ marginRight: 'auto', fontSize: 9.5, color: 'rgba(100,116,139,0.45)' }}>
              {providerLabel(provider)}
            </span>
          )}
          <button
            onClick={() => { void openExternal(imageUrl); window.dispatchEvent(new CustomEvent('athena-image-opened')); }}
            style={{ padding: '4px 10px', fontSize: 11, background: 'none', border: '1px solid rgba(56,189,248,0.16)', color: 'rgba(148,163,184,0.58)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
            Abrir
          </button>
          <button
            onClick={() => { navigator.clipboard?.writeText(imageUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
            style={{ padding: '4px 10px', fontSize: 11, background: copied ? 'rgba(56,189,248,0.09)' : 'none', border: `1px solid ${copied ? 'rgba(56,189,248,0.32)' : 'rgba(56,189,248,0.16)'}`, color: copied ? '#38bdf8' : 'rgba(148,163,184,0.58)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .18s ease' }}>
            {copied ? '✓ Copiado' : 'Copiar link'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function ImageGenerationCard({ imageUrl, prompt, isLoading, isError, isMobile = false, provider }) {
  useEffect(() => { injectKF(); }, []);
  const height = isMobile ? 180 : 230;

  return (
    <div style={{ margin: '8px 0', animation: 'igcFadeUp .35s ease' }}>
      {/* Label row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ opacity: 0.5, flexShrink: 0 }}>
          <rect x="1" y="1" width="10" height="10" rx="2" stroke="rgba(56,189,248,0.8)" strokeWidth="1"/>
          <circle cx="3.5" cy="3.5" r="1" fill="rgba(56,189,248,0.8)"/>
          <path d="M 1 9 L 3.5 6.5 L 5.5 8 L 7.5 5 L 11 8.5" stroke="rgba(56,189,248,0.8)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span style={{ fontSize: 10.5, color: 'rgba(100,116,139,0.52)', letterSpacing: '0.04em' }}>
          {isLoading ? 'Criando imagem…' : isError ? 'Geração de imagem' : 'Referência visual — Athena'}
        </span>
      </div>

      {isLoading ? (
        <LoadingView height={height} />
      ) : isError ? (
        <ErrorView prompt={prompt} height={height} />
      ) : imageUrl ? (
        <SuccessView imageUrl={imageUrl} prompt={prompt} provider={provider} isMobile={isMobile} />
      ) : (
        <ErrorView prompt={prompt} height={height} />
      )}
    </div>
  );
}
